import { LocateFixedIcon } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";

import { Button } from "@/web/components/primitives/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/web/components/primitives/tooltip";
import { Map, type MapBounds, type MapHandle, type MapPin } from "@/web/components/styled/Map/Map";
import { type ClientLocation, useClientLocation } from "@/web/hooks/useClientLocation";
import { cn } from "@/web/lib/styling";
import { BrowseCardLazy } from "@/web/routes/browse/-components/cards/BrowseCardLazy";
import {
  getBrowseResultKey,
  getBrowseResultKind,
} from "@/web/routes/browse/-components/mapBrowseResults";
import type { BrowseResult } from "@/web/routes/browse/-components/types";

// Pin colors distinguishing the two kinds of result. Live fundraising campaigns
// read as the brand blue; community nominations as the brand yellow.
const CAMPAIGN_COLOR = "var(--color-withus-blue)";
const NOMINATION_COLOR = "var(--color-withus-yellow)";

const LEGEND: { label: string; color: string }[] = [
  { label: "Campaigns", color: CAMPAIGN_COLOR },
  { label: "Nominations", color: NOMINATION_COLOR },
];

// Half-extent (km) of the box "Near Me" snaps to — a city-sized window around the
// user regardless of where the pins sit. One degree of latitude ≈ 111 km; a
// degree of longitude shrinks with latitude, so scale it by cos(lat).
const NEAR_ME_RADIUS_KM = 25;
const KM_PER_DEG_LAT = 111;

function boundsAround(latitude: number, longitude: number): MapBounds {
  const latPad = NEAR_ME_RADIUS_KM / KM_PER_DEG_LAT;
  // Floor cos() so longitudes near the poles can't blow the padding up to ∞.
  const cosLat = Math.max(Math.cos((latitude * Math.PI) / 180), 0.01);
  const lonPad = NEAR_ME_RADIUS_KM / (KM_PER_DEG_LAT * cosLat);
  return [
    [longitude - lonPad, latitude - latPad],
    [longitude + lonPad, latitude + latPad],
  ];
}

export function BrowseMap({ results }: { results: BrowseResult[] }) {
  // Live DOM node of each pin's popup card wrapper, keyed by pin id. Only the
  // active pin's card is mounted at a time, so a pin click can forward to that
  // card's own action without lifting each card's internal dialog state up here
  // (the card keeps owning its action, exactly as it does in the grid view).
  // (Keyed record rather than a `Map` — the name `Map` is the map component here.)
  const cardWrapperRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Imperative handle so the "Near Me" button can snap the camera to the user.
  const mapRef = useRef<MapHandle>(null);
  const handleNearMe = useCallback((location: ClientLocation) => {
    mapRef.current?.focusBounds(boundsAround(location.latitude, location.longitude));
  }, []);

  const activatePin = useCallback((key: string) => {
    // Replay the card's primary click by clicking the element it tags with
    // `data-browse-card-action` (a nomination's pledge dialog, a sharable
    // card's link, …). No-op for cards without an action, or before the lazy
    // card chunk has mounted. The Map keeps this pin's popup open on click, so
    // any modal the card opens stays mounted after the cursor leaves the pin.
    cardWrapperRefs.current[key]?.querySelector<HTMLElement>("[data-browse-card-action]")?.click();
  }, []);

  const pins = useMemo<MapPin[]>(
    () =>
      results.flatMap((result) => {
        if (result.latitude === undefined || result.longitude === undefined) return [];
        const kind = getBrowseResultKind(result);
        const key = getBrowseResultKey(result);
        return [
          {
            id: key,
            latitude: result.latitude,
            longitude: result.longitude,
            color: kind === "nomination" ? NOMINATION_COLOR : CAMPAIGN_COLOR,
            // Clicking the pin does the same thing as clicking the card in its
            // popup; also makes the pin's cursor a pointer.
            onClick: () => activatePin(key),
            // The full grid card, lazy-loaded on first hover. `hoverContent` is
            // only mounted for the active pin, so the heavy card chunk loads on
            // demand (the JSX here is cheap until mounted).
            hoverContent: (
              <div
                ref={(el) => {
                  cardWrapperRefs.current[key] = el;
                }}
                className="w-[300px]"
              >
                <BrowseCardLazy result={result} />
              </div>
            ),
          },
        ];
      }),
    [results, activatePin],
  );

  if (pins.length === 0) {
    return (
      <div className="flex h-[70vh] w-full items-center justify-center rounded-none border border-border px-4 text-center text-sm text-withus-dark-blue/60">
        No mappable results.
      </div>
    );
  }

  return (
    <Map ref={mapRef} pins={pins} className="h-[70vh]">
      <MapLegend />
      <NearMeButton onNearMe={handleNearMe} />
    </Map>
  );
}

/**
 * Snaps the map to a box around the viewer's (IP-derived) location. Disabled with
 * an explanatory tooltip until a location is available — the underlying lookup is
 * coarse, best-effort, and may never resolve (blocked requests, VPNs, etc.).
 */
function NearMeButton({ onNearMe }: { onNearMe: (location: ClientLocation) => void }) {
  const { ipLocation, ipStatus } = useClientLocation();
  const disabled = ipLocation === undefined;

  const button = (
    <Button
      type="button"
      variant="solid"
      color="white"
      size="sm"
      disabled={disabled}
      onClick={() => ipLocation && onNearMe(ipLocation)}
      className="rounded-none border border-border shadow-sm"
    >
      <LocateFixedIcon />
      Near Me
    </Button>
  );

  if (!disabled) {
    return <div className="absolute top-20 right-2 z-10">{button}</div>;
  }

  const message =
    ipStatus === "loading"
      ? "Finding your approximate location…"
      : "We couldn't determine your location.";

  return (
    <div className="absolute top-20 right-2 z-10">
      <Tooltip>
        {/* A wrapper element keeps the tooltip reachable: a disabled <button>
            emits no pointer events, so Radix can't hang the trigger off it. */}
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-not-allowed">{button}</span>
        </TooltipTrigger>
        <TooltipContent color="white" side="left">
          {message}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

/** Color key for the pins, overlaid in the map's top-left corner. */
function MapLegend() {
  return (
    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1 rounded-none bg-withus-pure-white/90 p-2 text-xs shadow-sm">
      {LEGEND.map(({ label, color }) => (
        <div key={label} className="flex items-center gap-2">
          <span
            className={cn("size-2.5 shrink-0 rounded-none border border-[#1a1a1a]")}
            style={{ backgroundColor: color }}
          />
          <span className="text-withus-dark-blue">{label}</span>
        </div>
      ))}
    </div>
  );
}
